import {
  useCallback,
  useState,
  useRef,
  useEffect,
  useMemo,
  useId,
  forwardRef,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';

import styles from './Multiselect.module.css';
import { Button } from '../Button';
import { Message } from '../Message';

import type { MultiselectProps, FontSizeValue } from './Multiselect.types';

/**
 * Font size token to CSS variable mapping
 */
const FONT_SIZE_TOKENS: Record<string, string> = {
  xs: 'var(--font-size-xs, 12px)',
  sm: 'var(--font-size-sm, 14px)',
  base: 'var(--font-size-base, 14px)',
  md: 'var(--font-size-md, 14.5px)',
  lg: 'var(--font-size-lg, 15px)',
  xl: 'var(--font-size-xl, 18px)',
};

/**
 * Get size-based default font size CSS value (sm: xs/12px, md: sm/14px)
 */
const getDefaultFontSize = (size: 'sm' | 'md'): string => {
  return size === 'sm'
    ? (FONT_SIZE_TOKENS.xs as string)
    : (FONT_SIZE_TOKENS.sm as string);
};

/**
 * Resolve font size value to CSS string, with optional default
 */
const resolveFontSizeWithDefault = (
  fontSize: FontSizeValue | undefined,
  defaultFontSize: string
): string => {
  if (!fontSize) return defaultFontSize;
  
  // Check if it's a theme token key
  if (typeof fontSize === 'string' && fontSize in FONT_SIZE_TOKENS) {
    return FONT_SIZE_TOKENS[fontSize] as string;
  }
  
  // Otherwise, treat as CSS value
  return typeof fontSize === 'number' ? `${fontSize}px` : String(fontSize);
};

/**
 * Multiselect component for selecting multiple options
 * Displays selected values as chips with individual removal
 *
 * @example
 * ```tsx
 * const options = [
 *   { value: 'apple', label: 'Apple' },
 *   { value: 'banana', label: 'Banana' },
 *   { value: 'cherry', label: 'Cherry' },
 * ];
 *
 * <Multiselect
 *   options={options}
 *   value={selectedValues}
 *   onChange={setSelectedValues}
 *   placeholder="Select fruits..."
 *   searchable
 * />
 * ```
 */
export const Multiselect = forwardRef<HTMLDivElement, MultiselectProps>(
  (
    {
      options,
      value,
      onChange,
      label,
      placeholder = 'Select...',
      disabled = false,
      required = false,
      error = false,
      errorMessage,
      size = 'md',
      maxDisplayedChips = 3,
      clearable = true,
      showBulkActions = false,
      selectAllLabel = 'Select All',
      clearAllLabel = 'Clear All',
      searchable = false,
      searchPlaceholder = 'Search...',
      fullWidth = false,
      className,
      'aria-label': ariaLabel,
      'data-testid': testId,
      dataTestId,
      dataId,
      // Font size customization props
      labelFontSize,
      chipFontSize,
      optionFontSize,
    },
    ref
  ) => {
    const componentId = useId();
    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const optionsRef = useRef<HTMLUListElement>(null);

    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    // Compute size-based default font size
    const defaultFontSize = getDefaultFontSize(size);
    
    // Compute resolved font sizes with size-based defaults
    const resolvedLabelFontSize = resolveFontSizeWithDefault(labelFontSize, defaultFontSize);
    const resolvedChipFontSize = resolveFontSizeWithDefault(chipFontSize, defaultFontSize);
    const resolvedOptionFontSize = resolveFontSizeWithDefault(optionFontSize, defaultFontSize);

    // Get selected options
    const selectedOptions = useMemo(() => {
      return options.filter((opt) => value.includes(opt.value));
    }, [options, value]);

    // Filter options based on search query
    const filteredOptions = useMemo(() => {
      if (!searchable || !searchQuery) {
        return options;
      }
      const lowerQuery = searchQuery.toLowerCase();
      return options.filter((option) =>
        option.label.toLowerCase().includes(lowerQuery)
      );
    }, [options, searchQuery, searchable]);

    // Handle click outside to close dropdown
    useEffect(() => {
      const handleClickOutside = (event: globalThis.MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
          setSearchQuery('');
          setHighlightedIndex(-1);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    // Focus search input when dropdown opens
    useEffect(() => {
      if (isOpen && searchable && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, [isOpen, searchable]);

    // Reset highlighted index when filtered options change
    useEffect(() => {
      setHighlightedIndex(-1);
    }, [filteredOptions.length]);

    // Toggle dropdown
    const handleToggle = useCallback(() => {
      if (disabled) return;
      setIsOpen((prev) => !prev);
      setSearchQuery('');
      setHighlightedIndex(-1);
    }, [disabled]);

    // Select or deselect an option
    const handleOptionClick = useCallback(
      (optionValue: string, optionDisabled?: boolean) => {
        if (optionDisabled || disabled) return;

        const newValue = value.includes(optionValue)
          ? value.filter((v) => v !== optionValue)
          : [...value, optionValue];

        onChange(newValue);
      },
      [value, onChange, disabled]
    );

    // Remove a single chip
    const handleRemoveChip = useCallback(
      (e: MouseEvent, optionValue: string) => {
        e.stopPropagation();
        if (disabled) return;
        onChange(value.filter((v) => v !== optionValue));
      },
      [value, onChange, disabled]
    );

    // Clear all selections
    const handleClearAll = useCallback(
      (e: MouseEvent) => {
        e.stopPropagation();
        if (disabled) return;
        onChange([]);
        setIsOpen(false);
      },
      [onChange, disabled]
    );

    // Select all options (only non-disabled ones)
    const handleSelectAll = useCallback(
      (e: MouseEvent) => {
        e.stopPropagation();
        if (disabled) return;
        const selectableValues = options
          .filter((opt) => !opt.disabled)
          .map((opt) => opt.value);
        onChange(selectableValues);
      },
      [options, onChange, disabled]
    );

    // Clear all in bulk actions (doesn't close dropdown)
    const handleBulkClearAll = useCallback(
      (e: MouseEvent) => {
        e.stopPropagation();
        if (disabled) return;
        onChange([]);
      },
      [onChange, disabled]
    );

    // Check if all selectable options are selected
    const allSelected = useMemo(() => {
      const selectableOptions = options.filter((opt) => !opt.disabled);
      return (
        selectableOptions.length > 0 &&
        selectableOptions.every((opt) => value.includes(opt.value))
      );
    }, [options, value]);

    // Keyboard navigation
    const handleKeyDown = useCallback(
      (e: KeyboardEvent) => {
        if (disabled) return;

        switch (e.key) {
          case 'Enter':
          case ' ':
            e.preventDefault();
            if (!isOpen) {
              setIsOpen(true);
            } else if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
              const option = filteredOptions[highlightedIndex];
              if (option && !option.disabled) {
                handleOptionClick(option.value, option.disabled);
              }
            }
            break;

          case 'Escape':
            e.preventDefault();
            setIsOpen(false);
            setSearchQuery('');
            setHighlightedIndex(-1);
            triggerRef.current?.focus();
            break;

          case 'ArrowDown':
            e.preventDefault();
            if (!isOpen) {
              setIsOpen(true);
            } else {
              setHighlightedIndex((prev) => {
                const nextIndex = prev + 1;
                return nextIndex >= filteredOptions.length ? 0 : nextIndex;
              });
            }
            break;

          case 'ArrowUp':
            e.preventDefault();
            if (isOpen) {
              setHighlightedIndex((prev) => {
                const nextIndex = prev - 1;
                return nextIndex < 0 ? filteredOptions.length - 1 : nextIndex;
              });
            }
            break;

          case 'Home':
            e.preventDefault();
            if (isOpen) {
              setHighlightedIndex(0);
            }
            break;

          case 'End':
            e.preventDefault();
            if (isOpen) {
              setHighlightedIndex(filteredOptions.length - 1);
            }
            break;

          default:
            break;
        }
      },
      [disabled, isOpen, highlightedIndex, filteredOptions, handleOptionClick]
    );

    // Scroll highlighted option into view
    useEffect(() => {
      if (highlightedIndex >= 0 && optionsRef.current) {
        const options = optionsRef.current.querySelectorAll('[role="option"]');
        const highlighted = options[highlightedIndex] as HTMLElement | undefined;
        if (highlighted) {
          highlighted.scrollIntoView({ block: 'nearest' });
        }
      }
    }, [highlightedIndex]);

    // Determine which chips to display
    const displayedChips = selectedOptions.slice(0, maxDisplayedChips);
    const remainingCount = selectedOptions.length - maxDisplayedChips;

    // Build container class names
    const containerClassNames = [styles.multiselect, className]
      .filter(Boolean)
      .join(' ');

    return (
      <div
        ref={(node) => {
          (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
          if (typeof ref === 'function') {
            ref(node);
          } else if (ref) {
            (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
          }
        }}
        className={containerClassNames}
        data-full-width={fullWidth}
        data-testid={dataTestId || testId}
        data-id={dataId}
      >
        {/* Label */}
        {label && (
          <label
            id={`${componentId}-label`}
            className={styles.label}
            htmlFor={`${componentId}-trigger`}
            style={{ fontSize: resolvedLabelFontSize }}
          >
            {label}
            {required && <span className={styles.requiredIndicator} aria-hidden="true">*</span>}
          </label>
        )}

        {/* Select wrapper */}
        <div className={styles.selectWrapper}>
          {/* Trigger button */}
          <button
            ref={triggerRef}
            type="button"
            id={`${componentId}-trigger`}
            className={styles.trigger}
            onClick={handleToggle}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            data-size={size}
            data-open={isOpen}
            data-error={error}
            data-disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-labelledby={label ? `${componentId}-label` : undefined}
            aria-label={ariaLabel || label}
            aria-controls={`${componentId}-listbox`}
            aria-required={required}
            data-testid={testId && `${testId}-trigger`}
          >
            <div className={styles.triggerContent}>
              {selectedOptions.length === 0 ? (
                <span
                  className={styles.placeholder}
                  style={{ fontSize: resolvedOptionFontSize }}
                >
                  {placeholder}
                </span>
              ) : (
                <div className={styles.chips}>
                  {displayedChips.map((option) => (
                    <span
                      key={option.value}
                      className={styles.chip}
                      data-size={size}
                      style={{ fontSize: resolvedChipFontSize }}
                    >
                      <span className={styles.chipLabel}>{option.label}</span>
                      <span
                        role="button"
                        className={styles.chipRemove}
                        onClick={(e) => handleRemoveChip(e, option.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleRemoveChip(e as unknown as MouseEvent, option.value);
                          }
                        }}
                        aria-label={`Remove ${option.label}`}
                        tabIndex={-1}
                        data-testid={testId && `${testId}-chip-remove-${option.value}`}
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          aria-hidden="true"
                        >
                          <path
                            d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                    </span>
                  ))}
                  {remainingCount > 0 && (
                    <span className={styles.moreChips}>+{remainingCount} more</span>
                  )}
                </div>
              )}
            </div>

            <div className={styles.triggerActions}>
              {/* Clear all button */}
              {clearable && selectedOptions.length > 0 && (
                <span
                  role="button"
                  className={styles.clearButton}
                  onClick={handleClearAll}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleClearAll(e as unknown as MouseEvent);
                    }
                  }}
                  aria-label="Clear all selections"
                  tabIndex={-1}
                  data-testid={testId && `${testId}-clear`}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M12 4L4 12M4 4L12 12"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              )}

              {/* Chevron */}
              <span className={styles.chevron} data-open={isOpen} aria-hidden="true">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5 7.5L10 12.5L15 7.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </div>
          </button>

          {/* Dropdown */}
          <div
            className={styles.dropdown}
            data-open={isOpen}
            role="presentation"
          >
            {/* Search input */}
            {searchable && (
              <div className={styles.searchWrapper}>
                <input
                  ref={searchInputRef}
                  type="text"
                  className={styles.searchInput}
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  aria-label="Search options"
                  data-testid={testId && `${testId}-search`}
                />
              </div>
            )}

            {/* Bulk actions */}
            {showBulkActions && (
              <div className={styles.bulkActions}>
                <Button
                  variant="ghost"
                  size="sm"
                  fontWeight="normal"
                  className={styles.bulkActionButton}
                  onClick={handleSelectAll}
                  disabled={allSelected}
                  dataTestId={testId ? `${testId}-select-all` : undefined}
                >
                  {selectAllLabel}
                </Button>
                <span className={styles.bulkActionDivider}>|</span>
                <Button
                  variant="ghost"
                  size="sm"
                  fontWeight="normal"
                  className={styles.bulkActionButton}
                  onClick={handleBulkClearAll}
                  disabled={value.length === 0}
                  dataTestId={testId ? `${testId}-clear-all` : undefined}
                >
                  {clearAllLabel}
                </Button>
              </div>
            )}

            {/* Options list */}
            <ul
              ref={optionsRef}
              id={`${componentId}-listbox`}
              className={styles.optionsList}
              role="listbox"
              aria-multiselectable="true"
              aria-labelledby={label ? `${componentId}-label` : undefined}
              tabIndex={-1}
            >
              {filteredOptions.length === 0 ? (
                <li className={styles.noResults}>No options found</li>
              ) : (
                filteredOptions.map((option, index) => {
                  const isSelected = value.includes(option.value);
                  const isHighlighted = index === highlightedIndex;

                  return (
                    <li
                      key={option.value}
                      role="option"
                      className={styles.option}
                      onClick={() => handleOptionClick(option.value, option.disabled)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      data-highlighted={isHighlighted}
                      data-disabled={option.disabled}
                      aria-selected={isSelected}
                      aria-disabled={option.disabled}
                      data-testid={testId && `${testId}-option-${option.value}`}
                      style={{ fontSize: resolvedOptionFontSize }}
                    >
                      {/* Checkbox */}
                      <span
                        className={styles.checkbox}
                        data-checked={isSelected}
                        aria-hidden="true"
                      >
                        {isSelected && (
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className={styles.checkIcon}
                          >
                            <path
                              d="M10 3L4.5 8.5L2 6"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </span>

                      {/* Option icon */}
                      {option.icon && (
                        <span className={styles.optionIcon} aria-hidden="true">
                          {option.icon}
                        </span>
                      )}

                      {/* Option label */}
                      <span className={styles.optionLabel}>{option.label}</span>
                    </li>
                  );
                })
              )}
            </ul>
          </div>
        </div>

        {/* Error message */}
        {error && errorMessage && (
          <Message variant="error" size="sm" dataTestId={testId ? `${testId}-error` : undefined}>
            {errorMessage}
          </Message>
        )}
      </div>
    );
  }
);

Multiselect.displayName = 'Multiselect';

export default Multiselect;